/**
 * ScraperHealthWidget — Shows real-time health of all 16 sportsbook scrapers
 * Reads from scraper_status table (Supabase Realtime subscription)
 * Prefix: shw-
 */
import { useState, useEffect, useCallback } from 'react'
import { appCoreSupabase, liveOddsSupabase } from '../lib/supabase'
import { fetchReceiverStats } from '../lib/api'
import { getBookLogo } from '../lib/logoMaps'
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, Activity, Wifi, WifiOff } from 'lucide-react'
import './ScraperHealthWidget.css'

const STATUS_CONFIG = {
  online:  { color: '#00C853', icon: CheckCircle,   label: 'Online' },
  offline: { color: '#ff4444', icon: XCircle,        label: 'Offline' },
  error:   { color: '#FFD600', icon: AlertTriangle,  label: 'Error' },
  unknown: { color: '#666',    icon: Clock,          label: 'Unknown' },
}

function timeAgo(dateStr) {
  if (!dateStr) return 'never'
  const diff = Date.now() - new Date(dateStr).getTime()
  if (diff < 5000) return 'just now'
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return `${Math.floor(diff / 86400000)}d ago`
}

export default function ScraperHealthWidget() {
  const [scrapers, setScrapers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedBook, setExpandedBook] = useState(null)
  const [logs, setLogs] = useState({})
  const [sortBy, setSortBy] = useState('name') // name, status, recency

  const fetchScrapers = useCallback(async () => {
    try {
      let rows = []

      if (liveOddsSupabase) {
        const { data, error: err } = await liveOddsSupabase
          .from('scraper_status')
          .select('*')
          .order('bookmaker')

        if (!err && (data || []).length > 0) {
          rows = data || []
        }
      }

      if (rows.length === 0) {
        const receiverResult = await fetchReceiverStats()
        if (receiverResult.status !== 'ok') throw new Error(receiverResult.message || 'Receiver stats unavailable')
        const nowIso = new Date().toISOString()
        rows = Object.entries(receiverResult.data?.by_bookmaker || {})
          .map(([bookmaker, outcomes_count]) => ({
            bookmaker,
            status: 'online',
            outcomes_count: Number(outcomes_count) || 0,
            delta_count: 0,
            cycle_time_ms: null,
            sports_scraped: [],
            updated_at: nowIso,
            last_success: nowIso,
            last_error: null,
            last_error_message: null,
            total_scrapes: 0,
            total_errors: 0,
          }))
          .sort((a, b) => a.bookmaker.localeCompare(b.bookmaker))
      }

      setScrapers(rows)
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchScrapers()
    const interval = setInterval(fetchScrapers, 15000)

    return () => {
      clearInterval(interval)
    }
  }, [fetchScrapers])

  const fetchLogs = useCallback(async (bookmaker) => {
    if (!appCoreSupabase) return
    const { data } = await appCoreSupabase
      .from('scraper_logs')
      .select('*')
      .eq('bookmaker', bookmaker)
      .order('created_at', { ascending: false })
      .limit(20)
    setLogs(prev => ({ ...prev, [bookmaker]: data || [] }))
  }, [])

  const handleExpand = (bookmaker) => {
    if (expandedBook === bookmaker) {
      setExpandedBook(null)
    } else {
      setExpandedBook(bookmaker)
      if (!logs[bookmaker]) fetchLogs(bookmaker)
    }
  }

  const onlineCount = scrapers.filter(s => s.status === 'online').length
  const totalOutcomes = scrapers.reduce((sum, s) => sum + (s.outcomes_count || 0), 0)
  const totalDeltas = scrapers.reduce((sum, s) => sum + (s.delta_count || 0), 0)

  const sorted = [...scrapers].sort((a, b) => {
    if (sortBy === 'status') {
      const order = { online: 0, error: 1, offline: 2, unknown: 3 }
      return (order[a.status] || 3) - (order[b.status] || 3)
    }
    if (sortBy === 'recency') {
      return new Date(b.last_success || 0) - new Date(a.last_success || 0)
    }
    return a.bookmaker.localeCompare(b.bookmaker)
  })

  return (
    <div className="widget shw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          {onlineCount > 0 ? <Wifi size={16} /> : <WifiOff size={16} />}
          Scraper Health
          <span className="shw-online-badge">
            {onlineCount}/{scrapers.length} Online
          </span>
        </h3>
        <button className="widget-refresh-btn" onClick={fetchScrapers} title="Refresh">
          <RefreshCw size={14} className={loading ? 'shw-spinner' : ''} />
        </button>
      </div>

      {/* Summary stats */}
      <div className="shw-stats">
        <div className="shw-stat">
          <Activity size={12} />
          <span>{totalOutcomes.toLocaleString()} total odds</span>
        </div>
        <div className="shw-stat">
          <RefreshCw size={12} />
          <span>{totalDeltas.toLocaleString()} deltas/cycle</span>
        </div>
        <div className="shw-stat-spacer" />
        <div className="shw-sort">
          <span>Sort:</span>
          <select className="shw-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="status">Status</option>
            <option value="recency">Recent</option>
          </select>
        </div>
      </div>

      {error && <div className="shw-error">{error}</div>}

      {loading && scrapers.length === 0 ? (
        <div className="shw-loading">Loading scraper status...</div>
      ) : (
        <div className="shw-list">
          {sorted.map(s => {
            const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.unknown
            const StatusIcon = cfg.icon
            const isExpanded = expandedBook === s.bookmaker

            return (
              <div key={s.bookmaker} className={`shw-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="shw-card-row" onClick={() => handleExpand(s.bookmaker)}>
                  <div className="shw-card-status" style={{ color: cfg.color }}>
                    <StatusIcon size={16} />
                  </div>
                  {getBookLogo(s.bookmaker) && (
                    <img src={getBookLogo(s.bookmaker)} alt="" className="shw-book-logo" onError={e => { e.target.style.display = 'none' }} />
                  )}
                  <div className="shw-card-name">{s.bookmaker}</div>
                  <div className="shw-card-metrics">
                    {s.status === 'online' && (
                      <>
                        <span className="shw-metric">{s.outcomes_count || 0} odds</span>
                        <span className="shw-metric-sep">|</span>
                        <span className="shw-metric">{s.delta_count || 0} Δ</span>
                        {s.cycle_time_ms && (
                          <>
                            <span className="shw-metric-sep">|</span>
                            <span className="shw-metric">{s.cycle_time_ms}ms</span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="shw-card-time" title={s.last_success || 'Never'}>
                    {timeAgo(s.last_success || s.updated_at)}
                  </div>
                </div>

                {isExpanded && (
                  <div className="shw-card-details">
                    <div className="shw-detail-grid">
                      <div className="shw-detail">
                        <span className="shw-detail-label">Total Scrapes</span>
                        <span className="shw-detail-value">{(s.total_scrapes || 0).toLocaleString()}</span>
                      </div>
                      <div className="shw-detail">
                        <span className="shw-detail-label">Total Errors</span>
                        <span className="shw-detail-value shw-detail-error">{s.total_errors || 0}</span>
                      </div>
                      <div className="shw-detail">
                        <span className="shw-detail-label">Sports</span>
                        <span className="shw-detail-value">
                          {(s.sports_scraped || []).join(', ') || 'none'}
                        </span>
                      </div>
                      <div className="shw-detail">
                        <span className="shw-detail-label">Last Error</span>
                        <span className="shw-detail-value shw-detail-error">
                          {s.last_error ? timeAgo(s.last_error) : 'none'}
                        </span>
                      </div>
                    </div>

                    {s.last_error_message && (
                      <div className="shw-error-msg">{s.last_error_message}</div>
                    )}

                    {logs[s.bookmaker] && logs[s.bookmaker].length > 0 && (
                      <div className="shw-logs">
                        <div className="shw-logs-title">Recent Logs</div>
                        {logs[s.bookmaker].map(log => (
                          <div key={log.id} className={`shw-log shw-log-${log.level}`}>
                            <span className="shw-log-time">
                              {new Date(log.created_at).toLocaleTimeString()}
                            </span>
                            <span className={`shw-log-level shw-level-${log.level}`}>
                              {log.level.toUpperCase()}
                            </span>
                            <span className="shw-log-msg">{log.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
